import { Box, chakra, Icon, IconButton, Input, InputGroup, InputRightElement, useColorModeValue, useId } from "@chakra-ui/react";
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { MdClose, MdKeyboardArrowDown } from "react-icons/md";

const TOUS = "Tous les clients";
const sansAccent = (texte: string) =>
  texte
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLocaleLowerCase("fr");

// Filtre client de toutes les listes : clients triés par ordre alphabétique, recherche à la frappe.
// `value` vaut "" quand aucun client n'est choisi
export default function ClientCombobox(props: { clients: ClientType[]; value: string; onChange: (id: string) => void }) {
  const { clients, value, onChange } = props;
  const [ouvert, setOuvert] = useState(false);
  // null tant que rien n'est saisi : le champ montre le client choisi, et la liste tous les clients
  const [recherche, setRecherche] = useState<string | null>(null);
  const [actif, setActif] = useState(0);
  const liste = useRef<HTMLUListElement>(null);
  const id = useId(undefined, "client-combobox");

  const tries = useMemo(
    () => [...clients].sort((a, b) => a.raison_sociale.localeCompare(b.raison_sociale, "fr", { sensitivity: "base" })),
    [clients]
  );
  const options = useMemo(() => {
    if (recherche?.trim()) {
      const texte = sansAccent(recherche.trim());
      return tries.filter((client) => sansAccent(client.raison_sociale).includes(texte));
    }
    return [{ id: "", raison_sociale: TOUS } as ClientType, ...tries];
  }, [recherche, tries]);
  const choisi = tries.find((client) => client.id === value);

  useEffect(() => {
    if (ouvert) {
      liste.current?.querySelector('[data-actif="true"]')?.scrollIntoView?.({ block: "nearest" });
    }
  }, [ouvert, actif]);

  function ouvrir() {
    setRecherche(null);
    // L'option « Tous les clients » occupe la première place
    setActif(value ? tries.findIndex((client) => client.id === value) + 1 : 0);
    setOuvert(true);
  }
  function fermer() {
    setOuvert(false);
    setRecherche(null);
  }
  function choisir(idClient: string) {
    if (idClient !== value) {
      onChange(idClient);
    }
    fermer();
  }
  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!ouvert) {
        ouvrir();
      } else if (options.length) {
        const pas = event.key === "ArrowDown" ? 1 : -1;
        setActif((actif + pas + options.length) % options.length);
      }
    } else if (event.key === "Enter" && ouvert) {
      event.preventDefault();
      if (options[actif]) {
        choisir(options[actif].id);
      }
    } else if (event.key === "Escape" && ouvert) {
      fermer();
    }
  }

  const fond = useColorModeValue("white", "navy.800");
  const survol = useColorModeValue("secondaryGray.300", "whiteAlpha.200");
  const bordure = useColorModeValue("gray.200", "whiteAlpha.300");
  const attenue = useColorModeValue("secondaryGray.600", "whiteAlpha.600");
  const actifId = ouvert && options[actif] ? `${id}-${options[actif].id || "tous"}` : undefined;

  return (
    <Box position="relative" w="240px" flexShrink={0}>
      <InputGroup size="sm">
        <Input
          role="combobox"
          aria-label="Filtrer par client"
          aria-autocomplete="list"
          aria-expanded={ouvert}
          aria-controls={`${id}-liste`}
          aria-activedescendant={actifId}
          autoComplete="off"
          borderRadius="10px"
          placeholder={TOUS}
          // Comme les autres filtres : « Tous les clients » est une valeur, pas une consigne
          _placeholder={{ color: "inherit" }}
          pe="28px"
          value={recherche ?? choisi?.raison_sociale ?? ""}
          onChange={(event) => {
            setRecherche(event.target.value);
            setActif(0);
            setOuvert(true);
          }}
          onFocus={(event) => event.target.select()}
          onClick={(event) => {
            if (!ouvert) {
              ouvrir();
              event.currentTarget.select();
            }
          }}
          onBlur={fermer}
          onKeyDown={handleKeyDown}
        />
        {value ? (
          <InputRightElement>
            <IconButton
              aria-label="Effacer le filtre client"
              icon={<Icon as={MdClose} />}
              size="xs"
              variant="ghost"
              borderRadius="full"
              // Garde le focus dans le champ
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choisir("")}
            />
          </InputRightElement>
        ) : (
          <InputRightElement pointerEvents="none">
            <Icon as={MdKeyboardArrowDown} boxSize="20px" />
          </InputRightElement>
        )}
      </InputGroup>
      {ouvert && (
        <chakra.ul
          ref={liste}
          id={`${id}-liste`}
          role="listbox"
          aria-label="Clients"
          listStyleType="none"
          position="absolute"
          top="100%"
          left="0"
          right="0"
          mt="4px"
          py="4px"
          maxH="280px"
          overflowY="auto"
          zIndex="dropdown"
          bg={fond}
          border="1px solid"
          borderColor={bordure}
          borderRadius="10px"
          boxShadow="lg"
          fontSize="sm"
        >
          {options.map((client, index) => (
            <chakra.li
              key={client.id || "tous"}
              id={`${id}-${client.id || "tous"}`}
              role="option"
              aria-selected={client.id === value}
              data-actif={index === actif}
              px="12px"
              py="5px"
              cursor="pointer"
              bg={index === actif ? survol : undefined}
              fontWeight={client.id === value ? "700" : "400"}
              // Garde le focus dans le champ jusqu'au clic
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActif(index)}
              onClick={() => choisir(client.id)}
            >
              {client.raison_sociale}
            </chakra.li>
          ))}
          {options.length === 0 && (
            <chakra.li role="option" aria-disabled="true" aria-selected={false} px="12px" py="5px" color={attenue}>
              Aucun client trouvé
            </chakra.li>
          )}
        </chakra.ul>
      )}
    </Box>
  );
}
